import React, { Fragment, useState, useCallback } from 'react';
import { List, ListSubheader, Icon, Button, Tooltip, Select, MenuItem } from '@mui/material';
import ScoreIcon from 'global/components/ScoreIcon';
import ChecklistScores, { ChecklistScoresPrint } from '../ChecklistScores';
import useStyles from './styles';
import { get, last, size } from 'global/lib/lodash';
import clsx from 'clsx';
import { getItemKey, getId } from './utils';
import Modal from 'global/components/modal';
import useRetrieveData from 'global/hooks/useRetrieveData';
import ImageGrid from 'global/components/image-grid';
import colors from 'global/styles/colors';
import { Button as Btn } from 'global/components/Button';
import { stopProp, _size, sortAlphaNum } from 'global/utils';
import ReportPhotos from '../ReportPhotos';
import { withRouter } from 'react-router-dom';
import { useSupersets } from 'global/hooks/useSupersets';
import { Text } from 'global/components/text';

export const ImageModal = (props) => {
  const { superset_id, assessment } = props;

  const [state, setState] = useState({
    superset_id,
  });

  const { supersets } = useSupersets({ assessment });

  const { data, loading, error } = useRetrieveData(
    { url: '/realm-photos' },
    {
      params: {
        filter: {
          superset_id: state.superset_id,
          assessment_id: get(assessment, 'id'),
        },
        include: 'supersets,images',
      },
      build: ['realmPhotos'],
      useEffect: true,
      transformData: (da) => {
        da = da.map((d) => ({ ...d, ...get(d, 'images.0', {}), assessment })).sort(sortAlphaNum);
        if (!state.superset_id) {
          return da.filter((d) => !size(d.supersets));
        }
        return da;
      },
    }
  );

  if (error) {
    return <div style={{ color: colors.redIcon }}>{error}</div>;
  }

  return (
    <div>
      <Select displayEmpty style={{ width: '100%' }} value={state.superset_id} onChange={(e) => setState((s) => ({ ...s, superset_id: e.target.value }))}>
        {[{ name: 'None', value: null }, ...supersets].map((s) => (
          <MenuItem key={s.id} value={s.id}>
            {s.name}
          </MenuItem>
        ))}
      </Select>
      <ImageGrid
        folders={data}
        includeSearch={false}
        loading={loading}
        userCanEdit={false}
        hideSuperset
        blankContent={() => <Text>There are no photos here; try changing the filters.</Text>}
        {...props}
      />
    </div>
  );
};

export const ImageSelectModal = withRouter((props) => {
  const [selected, setSelected] = useState({});

  const onSelectedChange = (sel) => {
    setSelected(
      Object.keys(sel)
        .filter((k) => !!sel[k])
        .map((k) => sel[k])
    );
  };

  const onClose = () => {
    props.onClose();
    setSelected({});
  };

  const onSubmit = () => {
    props.onSubmit && props.onSubmit({ images: selected });
    onClose();
  };

  const renderBtn = () => {
    if (!_size(selected)) {
      return { title: 'Add', disabled: true };
    }

    return {
      title: `Add ${_size(selected)}`,
      onClick: onSubmit,
    };
  };

  const IMAGEMODAL = props.useFullPhotos ? ReportPhotos : ImageModal;

  return (
    <Modal {...props} onClose={onClose} buttons={[renderBtn()]}>
      <IMAGEMODAL canSelect={true} {...props} _location={{ pathname: 'reports/photo' }} onSelectedChange={onSelectedChange} />
    </Modal>
  );
});

export const ImageSelectBtn = (props) => {
  const [modal, setModal] = useState({ open: false });

  const showImages = (e) => {
    stopProp(e);
    props.onOpen && props.onOpen();
    setModal({
      ...props,
      open: true,
      fullScreen: true,
      title: props.modalTitle || `Photos`,
      maxWidth: 920,
      onClose: () => {
        setModal((m) => ({ ...m, open: false }));
        props.onClose && props.onClose();
      },
    });
  };

  if (get(props, 'assessment.realmPhotoCount', 1) === 0) {
    return <></>;
  }

  return (
    <>
      <Tooltip title={props.tooltip ? props.tooltip : props.disabled ? 'Please select a system to access your photos' : 'Insert from media already uploaded to the Photos tab'}>
        <span>
          <Btn {...props} onClick={showImages}>
            <Icon>photo_library</Icon>
          </Btn>
        </span>
      </Tooltip>
      <ImageSelectModal {...modal} />
    </>
  );
};

export function SystemScore(props) {
  const { pssvSystem, setSidebarOpen, activeEdit, pssvChecklist, selectItem, selectedItems, showSelectBoxes, isCourse, notFirst, printView } = props;
  const classes = useStyles();
  const pChildren = get(pssvSystem, 'children', []).filter((f) => (activeEdit ? getId(pssvChecklist) === getId(f) : true));
  const isSelected = useCallback((item) => get(selectedItems, getItemKey(item)), [selectedItems]);
  const hasSelectedItems = Object.keys(selectedItems).filter((k) => get(selectedItems, [k, 'itemType']) === 'checkpoint').length > 0;

  const currentSelect = isSelected(pssvSystem);
  const indeterminate = Object.keys(selectedItems).filter((k) => get(selectedItems, [k, 'parentId']) == pssvSystem?.id).length > 0;

  const useCompareScore = get(props, 'filter.assessment_status') !== 'active';
  const useCompare = useCompareScore && get(props, 'filter.useDiff');

  const ScoreIconProps = {
    ...(!activeEdit
      ? {
          scorecard: props.scorecard,
          useBlankCheckbox: showSelectBoxes,
          selected: showSelectBoxes && get(currentSelect, 'fullCount'),
          indeterminate: showSelectBoxes && indeterminate,
          onClick: () => selectItem(pssvSystem),
          useCompare,
          compare: get(pssvSystem, 'compare', null),
          displayValue: get(pssvSystem, 'displayValue', null),
          canClick: false,
          filter: get(props, 'filter'),
        }
      : {}),

    className: clsx(classes.headerChild),
    score: useCompareScore && !useCompare ? get(pssvSystem, 'compare.value') : get(pssvSystem, 'value'),
    active: useCompareScore && !useCompare ? get(pssvSystem, 'compare') : pssvSystem,
  };

  return (
    <div className={notFirst ? classes.system : undefined} key={getId(pssvSystem)}>
      {!isCourse && (
        <ListSubheader className={classes.systemHeader} disableGutters={true}>
          <ScoreIcon {...ScoreIconProps} />
          {!useCompare && <h2 className={clsx(classes.headerChild, classes.headerChildScore)}>{get(ScoreIconProps, 'active.displayValue')}</h2>}
          <h2 className={classes.headerChild}>
            <span className={clsx(classes.headerChild, classes.headerChildName)}>{get(pssvSystem, 'name')}</span>
            {get(pssvSystem, 'criteria') && <span className={classes.headerCriteria}>{get(pssvSystem, 'criteria')}</span>}
          </h2>
          <div style={{ flexGrow: 1 }}></div>
          <Button className={classes.toggleSidebar} onClick={() => setSidebarOpen(true)}>
            <Icon>view_list</Icon>
          </Button>

          {!printView && (
            <ImageSelectBtn
              variant={'default'}
              tooltip={`${get(pssvSystem, 'name')} Photos`}
              className={classes.showImages}
              superset_id={get(pssvSystem, 'supersetId')}
              assessment={get(props, 'assessment.data')}
              canSelect={false}
            />
          )}
        </ListSubheader>
      )}
      <List>
        {pChildren.map((data, i) => (
          <Fragment key={data.id}>
            {!printView ? (
              <ChecklistScores
                {...props}
                pssvChecklist={data}
                isSelected={isSelected}
                hasSelectedItems={hasSelectedItems}
                next={(activeEdit && props.next) || get(pChildren, i + 1, get(props, 'next.children.0'))}
                prev={(activeEdit && props.prev) || get(pChildren, i - 1, last(get(props, 'prev.children')))}
              />
            ) : (
              <ChecklistScoresPrint {...props} notFirst={i !== 0} pssvChecklist={data} />
            )}
          </Fragment>
        ))}
      </List>
    </div>
  );
}
